import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { benchAlertsService } from '../../services/benchAlerts';
import useAuthStore from '../../store/authStore';

const BenchAlertContext = createContext({});

export const BenchAlertProvider = ({ children }) => {
  const { user } = useAuthStore();
  const [items, setItems] = useState([]);
  const [history, setHistory] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, overdue: 0 });
  const [loading, setLoading] = useState(true);

  // Determine if the user is an RMG approver (Super Admin excluded)
  const userRoles = Array.isArray(user?.roles)
    ? user.roles.map(r => (typeof r === 'string' ? r : r?.name || '').toUpperCase())
    : (typeof user?.roles === 'string' ? [user.roles.toUpperCase()] : []);

  const isSuperAdmin = userRoles.some(r =>
    ['SUPER ADMIN', 'SUPER_ADMIN', 'SUPERADMIN'].includes(r)
  );

  const isApprover = !isSuperAdmin && userRoles.some(r =>
    ['RMG LEAD', 'RMG TEAM', 'PMO LEAD', 'PMO TEAM', 'RMG_APPROVER'].includes(r)
  );

  const fetchAlerts = useCallback(async () => {
    if (!isApprover) {
      setLoading(false);
      return;
    }
    try {
      const [pendingRes, historyRes] = await Promise.all([
        benchAlertsService.getPending(),
        benchAlertsService.getHistory()
      ]);
      setItems(pendingRes.data.items);
      setHistory(historyRes.data.items);
      
      const pendingCount = pendingRes.data.count;
      const overdueCount = pendingRes.data.items.filter(i => i.is_overdue).length;
      setCounts({ pending: pendingCount, overdue: overdueCount });
    } catch (err) {
      console.error('Failed to fetch bench alerts:', err);
    } finally {
      setLoading(false);
    }
  }, [isApprover]);

  useEffect(() => {
    if (user) {
      fetchAlerts();
    }
  }, [user, fetchAlerts]);

  const onApprove = async (row) => {
    try {
      await benchAlertsService.approve(row.id, row.allocation_version);
      setItems(prev => prev.map(i => i.id === row.id ? { ...i, status: 'APPROVED' } : i));
      setCounts(c => ({ ...c, pending: Math.max(0, c.pending - 1) }));
      try {
        const historyRes = await benchAlertsService.getHistory();
        setHistory(historyRes.data.items || []);
      } catch (e) {
        console.error('Failed to update history after approve:', e);
      }
    } catch (err) {
      console.error('Failed to approve bench alert:', err);
      if (err.response?.status === 409) {
        alert('This allocation was modified elsewhere. Refreshing list.');
        fetchAlerts();
      }
    }
  };

  const onChangeDate = async (row, proposed_end_date, reason) => {
    try {
      await benchAlertsService.changeDate(row.id, {
        proposed_end_date,
        reason,
        allocation_version: row.allocation_version
      });
      setItems(prev => prev.map(i => i.id === row.id ? { ...i, status: 'DATE_CHANGED_APPROVED', effective_end_date: proposed_end_date } : i));
      setCounts(c => ({ ...c, pending: Math.max(0, c.pending - 1) }));
      try {
        const historyRes = await benchAlertsService.getHistory();
        setHistory(historyRes.data.items || []);
      } catch (e) {
        console.error('Failed to update history after date change:', e);
      }
    } catch (err) {
      console.error('Failed to change date on bench alert:', err);
      if (err.response?.status === 409) {
        alert('This allocation was modified elsewhere. Refreshing list.');
        fetchAlerts();
      } else {
        alert(err.response?.data?.detail || 'An error occurred.');
      }
    }
  };

  return (
    <BenchAlertContext.Provider value={{
      items, history, counts, loading, isApprover, onApprove, onChangeDate, refresh: fetchAlerts
    }}>
      {children}
    </BenchAlertContext.Provider>
  );
};

export const useBenchAlerts = () => useContext(BenchAlertContext);
